<script setup lang="ts">
import { useI18n } from "vue-i18n";
import MemberHeader from "@/components/member/MemberHeader.vue";
import DeveloperSidebar from "@/components/developer/DeveloperSidebar.vue";

const { t } = useI18n();
</script>
<template>
  <MemberHeader />

  <div class="flex h-screen bg-gray-50">
    <DeveloperSidebar />

    <main id="main-content" class="py-8">
      <div class="max-w-5xl mx-auto px-5">
        <section id="pin-header" class="mb-8">
          <div class="bg-white rounded-lg border border-gray-200 p-6">
            <div class="flex items-start justify-between">
              <div>
                <h1 class="text-3xl font-bold text-accent mb-2">
                  {{ $t('developer.project.submit_pin.title') }}
                </h1>
                <p class="text-gray-600 mb-4">
                  {{ $t('developer.project.submit_pin.subtitle') }}
                </p>
                <div class="flex items-center space-x-4 text-sm">
                  <span class="flex items-center text-primary">
                    <i class="fas fa-dollar-sign mr-1"></i>
                    {{ $t('developer.project.submit_pin.submission_fee') }}
                  </span>
                  <span class="flex items-center text-gray-600">
                    <i class="fas fa-clock mr-1"></i>
                    {{ $t('developer.project.submit_pin.review_sla') }}
                  </span>
                  <span class="flex items-center text-gray-600">
                    <i class="fas fa-info-circle mr-1"></i>
                    {{ $t('developer.project.submit_pin.auto_categorized') }}
                  </span>
                </div>
              </div>
              <div class="text-right">
                <div
                  class="bg-primary/10 px-3 py-1 rounded-full text-primary text-sm font-medium mb-2"
                >
                  {{ $t('developer.project.submit_pin.developer_id') }}: DIN-2024-001247
                </div>
                <button class="text-primary text-sm hover:underline">
                  <i class="fas fa-save mr-1"></i>{{ $t('developer.project.submit_pin.save_draft') }}
                </button>
              </div>
            </div>
          </div>
        </section>

        <form id="pin-form" class="space-y-8">
          <section
            id="project-details"
            class="bg-white rounded-lg border border-gray-200 p-6"
          >
            <h2
              class="text-xl font-semibold text-accent mb-6 flex items-center"
            >
              <i class="fas fa-project-diagram text-primary mr-3"></i>
              {{ $t('developer.project.submit_pin.project_details.title') }}
            </h2>

            <div class="grid grid-cols-1 lg:grid-cols-2 gap-6">
              <div>
                <label class="block text-sm font-medium text-gray-700 mb-2"
                  >{{ $t('developer.project.submit_pin.project_details.project_name') }} *</label
                >
                <input
                  type="text"
                  class="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-primary"
                  :placeholder="$t('developer.project.submit_pin.project_details.project_name_placeholder')"
                />
              </div>

              <div>
                <label class="block text-sm font-medium text-gray-700 mb-2"
                  >{{ $t('developer.project.submit_pin.project_details.project_type') }} *</label
                >
                <select
                  class="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-primary"
                >
                  <option>{{ $t('developer.project.submit_pin.project_details.select_type') }}</option>
                  <option>{{ $t('developer.project.submit_pin.project_details.types.afforestation') }}</option>
                  <option>{{ $t('developer.project.submit_pin.project_details.types.forest_management') }}</option>
                  <option>{{ $t('developer.project.submit_pin.project_details.types.renewable_energy') }}</option>
                  <option>{{ $t('developer.project.submit_pin.project_details.types.energy_efficiency') }}</option>
                  <option>{{ $t('developer.project.submit_pin.project_details.types.waste_management') }}</option>
                  <option>{{ $t('developer.project.submit_pin.project_details.types.agriculture') }}</option>
                </select>
              </div>

              <div class="lg:col-span-2">
                <label class="block text-sm font-medium text-gray-700 mb-2"
                  >{{ $t('developer.project.submit_pin.project_details.project_summary') }} *</label
                >
                <textarea
                  rows="4"
                  class="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-primary"
                  :placeholder="$t('developer.project.submit_pin.project_details.project_summary_placeholder')"
                ></textarea>
              </div>

              <div class="lg:col-span-2">
                <label class="block text-sm font-medium text-gray-700 mb-2"
                  >{{ $t('developer.project.submit_pin.project_details.project_scope') }} *</label
                >
                <textarea
                  rows="3"
                  class="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-primary"
                  :placeholder="$t('developer.project.submit_pin.project_details.project_scope_placeholder')"
                ></textarea>
              </div>
            </div>
          </section>

          <section
            id="location-details"
            class="bg-white rounded-lg border border-gray-200 p-6"
          >
            <h2
              class="text-xl font-semibold text-accent mb-6 flex items-center"
            >
              <i class="fas fa-map-marker-alt text-primary mr-3"></i>
              {{ $t('developer.project.submit_pin.location.title') }}
            </h2>

            <div class="grid grid-cols-1 lg:grid-cols-2 gap-6">
              <div>
                <label class="block text-sm font-medium text-gray-700 mb-2"
                  >{{ $t('developer.project.submit_pin.location.country') }} *</label
                >
                <select
                  class="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-primary"
                >
                  <option>{{ $t('developer.project.submit_pin.location.select_country') }}</option>
                  <option>United States</option>
                  <option>Canada</option>
                  <option>Brazil</option>
                  <option>Indonesia</option>
                </select>
              </div>

              <div>
                <label class="block text-sm font-medium text-gray-700 mb-2"
                  >{{ $t('developer.project.submit_pin.location.state_province') }} *</label
                >
                <input
                  type="text"
                  class="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-primary"
                  :placeholder="$t('developer.project.submit_pin.location.state_province_placeholder')"
                />
              </div>

              <div class="lg:col-span-2">
                <label class="block text-sm font-medium text-gray-700 mb-2"
                  >{{ $t('developer.project.submit_pin.location.location_description') }} *</label
                >
                <textarea
                  rows="2"
                  class="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-primary"
                  :placeholder="$t('developer.project.submit_pin.location.location_description_placeholder')"
                ></textarea>
              </div>

              <div>
                <label class="block text-sm font-medium text-gray-700 mb-2"
                  >{{ $t('developer.project.submit_pin.location.gps_latitude') }} *</label
                >
                <input
                  type="text"
                  class="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-primary"
                  :placeholder="$t('developer.project.submit_pin.location.gps_latitude_placeholder')"
                />
              </div>

              <div>
                <label class="block text-sm font-medium text-gray-700 mb-2"
                  >{{ $t('developer.project.submit_pin.location.gps_longitude') }} *</label
                >
                <input
                  type="text"
                  class="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-primary"
                  :placeholder="$t('developer.project.submit_pin.location.gps_longitude_placeholder')"
                />
              </div>

              <div class="lg:col-span-2">
                <div class="bg-gray-50 border border-gray-200 rounded-md p-4">
                  <div
                    class="flex items-center justify-center h-48 text-gray-500"
                  >
                    <div class="text-center">
                      <i class="fas fa-map text-4xl mb-2"></i>
                      <p>{{ $t('developer.project.submit_pin.location.interactive_map') }}</p>
                      <button
                        type="button"
                        class="mt-2 text-primary hover:underline text-sm"
                      >
                        {{ $t('developer.project.submit_pin.location.upload_kml') }}
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </section>

          <section
            id="emission-reduction"
            class="bg-white rounded-lg border border-gray-200 p-6"
          >
            <h2
              class="text-xl font-semibold text-accent mb-6 flex items-center"
            >
              <i class="fas fa-chart-line text-primary mr-3"></i>
              {{ $t('developer.project.submit_pin.emissions.title') }}
            </h2>

            <div class="grid grid-cols-1 lg:grid-cols-3 gap-6">
              <div>
                <label class="block text-sm font-medium text-gray-700 mb-2"
                  >{{ $t('developer.project.submit_pin.emissions.annual_reduction') }} *</label
                >
                <input
                  type="number"
                  class="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-primary"
                  placeholder="0"
                />
              </div>

              <div>
                <label class="block text-sm font-medium text-gray-700 mb-2"
                  >{{ $t('developer.project.submit_pin.emissions.project_duration') }} *</label
                >
                <input
                  type="number"
                  class="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-primary"
                  placeholder="0"
                />
              </div>

              <div>
                <label class="block text-sm font-medium text-gray-700 mb-2"
                  >{{ $t('developer.project.submit_pin.emissions.total_reduction') }}</label
                >
                <input
                  type="number"
                  class="w-full border border-gray-300 rounded-md px-3 py-2 bg-gray-50"
                  :placeholder="$t('developer.project.submit_pin.emissions.auto_calculated')"
                  readonly
                />
              </div>

              <div class="lg:col-span-3">
                <label class="block text-sm font-medium text-gray-700 mb-2"
                  >{{ $t('developer.project.submit_pin.emissions.methodology') }} *</label
                >
                <textarea
                  rows="3"
                  class="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-primary"
                  :placeholder="$t('developer.project.submit_pin.emissions.methodology_placeholder')"
                ></textarea>
              </div>
            </div>
          </section>

          <section
            id="required-uploads"
            class="bg-white rounded-lg border border-gray-200 p-6"
          >
            <h2
              class="text-xl font-semibold text-accent mb-6 flex items-center"
            >
              <i class="fas fa-upload text-primary mr-3"></i>
              {{ $t('developer.project.submit_pin.uploads.title') }}
            </h2>

            <div class="space-y-6">
              <div
                id="npramp-upload"
                class="border border-gray-200 rounded-lg p-4"
              >
                <div class="flex items-center justify-between mb-3">
                  <h3 class="font-medium text-accent">
                    {{ $t('developer.project.submit_pin.uploads.npramp') }}
                    *
                  </h3>
                  <span class="text-xs text-error">{{ $t('developer.project.submit_pin.uploads.required') }}</span>
                </div>
                <div
                  class="border-2 border-dashed border-gray-300 rounded-lg p-6 text-center hover:border-primary transition-colors cursor-pointer"
                >
                  <i
                    class="fas fa-cloud-upload-alt text-3xl text-gray-400 mb-2"
                  ></i>
                  <p class="text-gray-600 mb-2">
                    {{ $t('developer.project.submit_pin.uploads.upload_text') }}
                  </p>
                  <p class="text-xs text-gray-500">{{ $t('developer.project.submit_pin.uploads.file_types_doc') }}</p>
                </div>
              </div>

              <div
                id="ehmf-upload"
                class="border border-gray-200 rounded-lg p-4"
              >
                <div class="flex items-center justify-between mb-3">
                  <h3 class="font-medium text-accent">
                    {{ $t('developer.project.submit_pin.uploads.ehmf') }} *
                  </h3>
                  <span class="text-xs text-error">{{ $t('developer.project.submit_pin.uploads.required') }}</span>
                </div>
                <div
                  class="border-2 border-dashed border-gray-300 rounded-lg p-6 text-center hover:border-primary transition-colors cursor-pointer"
                >
                  <i
                    class="fas fa-cloud-upload-alt text-3xl text-gray-400 mb-2"
                  ></i>
                  <p class="text-gray-600 mb-2">
                    {{ $t('developer.project.submit_pin.uploads.upload_text') }}
                  </p>
                  <p class="text-xs text-gray-500">{{ $t('developer.project.submit_pin.uploads.file_types_doc') }}</p>
                </div>
              </div>

              <div
                id="spcp-upload"
                class="border border-gray-200 rounded-lg p-4"
              >
                <div class="flex items-center justify-between mb-3">
                  <h3 class="font-medium text-accent">
                    {{ $t('developer.project.submit_pin.uploads.spcp') }} *
                  </h3>
                  <span class="text-xs text-error">{{ $t('developer.project.submit_pin.uploads.required') }}</span>
                </div>
                <div
                  class="border-2 border-dashed border-gray-300 rounded-lg p-6 text-center hover:border-primary transition-colors cursor-pointer"
                >
                  <i
                    class="fas fa-cloud-upload-alt text-3xl text-gray-400 mb-2"
                  ></i>
                  <p class="text-gray-600 mb-2">
                    {{ $t('developer.project.submit_pin.uploads.upload_text') }}
                  </p>
                  <p class="text-xs text-gray-500">{{ $t('developer.project.submit_pin.uploads.file_types_doc') }}</p>
                </div>
              </div>

              <div
                id="fpic-upload"
                class="border border-gray-200 rounded-lg p-4"
              >
                <div class="flex items-center justify-between mb-3">
                  <h3 class="font-medium text-accent">
                    {{ $t('developer.project.submit_pin.uploads.fpic') }} *
                  </h3>
                  <span class="text-xs text-error">{{ $t('developer.project.submit_pin.uploads.required') }}</span>
                </div>
                <div
                  class="border-2 border-dashed border-gray-300 rounded-lg p-6 text-center hover:border-primary transition-colors cursor-pointer"
                >
                  <i
                    class="fas fa-cloud-upload-alt text-3xl text-gray-400 mb-2"
                  ></i>
                  <p class="text-gray-600 mb-2">
                    {{ $t('developer.project.submit_pin.uploads.upload_text') }}
                  </p>
                  <p class="text-xs text-gray-500">{{ $t('developer.project.submit_pin.uploads.file_types_doc') }}</p>
                </div>
              </div>

              <div
                id="financial-upload"
                class="border border-gray-200 rounded-lg p-4"
              >
                <div class="flex items-center justify-between mb-3">
                  <h3 class="font-medium text-accent">
                    {{ $t('developer.project.submit_pin.uploads.financial') }} *
                  </h3>
                  <span class="text-xs text-error">{{ $t('developer.project.submit_pin.uploads.required') }}</span>
                </div>
                <div
                  class="border-2 border-dashed border-gray-300 rounded-lg p-6 text-center hover:border-primary transition-colors cursor-pointer"
                >
                  <i
                    class="fas fa-cloud-upload-alt text-3xl text-gray-400 mb-2"
                  ></i>
                  <p class="text-gray-600 mb-2">
                    {{ $t('developer.project.submit_pin.uploads.upload_text') }}
                  </p>
                  <p class="text-xs text-gray-500">{{ $t('developer.project.submit_pin.uploads.file_types_finance') }}</p>
                </div>
              </div>
            </div>
          </section>

          <section
            id="declarations"
            class="bg-white rounded-lg border border-gray-200 p-6"
          >
            <h2
              class="text-xl font-semibold text-accent mb-6 flex items-center"
            >
              <i class="fas fa-check-circle text-primary mr-3"></i>
              {{ $t('developer.project.submit_pin.declarations.title') }}
            </h2>

            <div class="space-y-4">
              <label class="flex items-start space-x-3 cursor-pointer">
                <input
                  type="checkbox"
                  class="mt-1 rounded border-gray-300 text-primary focus:ring-primary"
                />
                <span class="text-sm text-gray-700"
                  >{{ $t('developer.project.submit_pin.declarations.accurate_info') }}</span
                >
              </label>

              <label class="flex items-start space-x-3 cursor-pointer">
                <input
                  type="checkbox"
                  class="mt-1 rounded border-gray-300 text-primary focus:ring-primary"
                />
                <span class="text-sm text-gray-700"
                  >{{ $t('developer.project.submit_pin.declarations.false_info_penalty') }}</span
                >
              </label>

              <label class="flex items-start space-x-3 cursor-pointer">
                <input
                  type="checkbox"
                  class="mt-1 rounded border-gray-300 text-primary focus:ring-primary"
                />
                <span class="text-sm text-gray-700"
                  >{{ $t('developer.project.submit_pin.declarations.fee_agreement') }}</span
                >
              </label>

              <label class="flex items-start space-x-3 cursor-pointer">
                <input
                  type="checkbox"
                  class="mt-1 rounded border-gray-300 text-primary focus:ring-primary"
                />
                <span class="text-sm text-gray-700"
                  >{{ $t('developer.project.submit_pin.declarations.review_period') }}</span
                >
              </label>
            </div>
          </section>

          <section
            id="submission-actions"
            class="bg-white rounded-lg border border-gray-200 p-6"
          >
            <div
              class="flex flex-col sm:flex-row items-center justify-between gap-4"
            >
              <div class="flex items-center space-x-4">
                <button
                  type="button"
                  class="px-6 py-2 border border-gray-300 text-gray-700 rounded-md hover:bg-gray-50"
                >
                  <i class="fas fa-save mr-2"></i>{{ $t('developer.project.submit_pin.actions.save_draft') }}
                </button>
                <button
                  type="button"
                  class="px-6 py-2 border border-primary text-primary rounded-md hover:bg-primary/5"
                >
                  <i class="fas fa-eye mr-2"></i>{{ $t('developer.project.submit_pin.actions.preview') }}
                </button>
              </div>

              <div class="flex items-center space-x-4">
                <div class="text-right text-sm text-gray-600">
                  <p>
                    {{ $t('developer.project.submit_pin.actions.submission_fee_label') }}
                    <span class="font-semibold text-primary">$500.00</span>
                  </p>
                  <p class="text-xs">{{ $t('developer.project.submit_pin.actions.payment_processed') }}</p>
                </div>
                <button
                  type="submit"
                  class="px-8 py-3 bg-primary text-white rounded-md font-medium hover:bg-primary/90 flex items-center"
                >
                  <i class="fas fa-paper-plane mr-2"></i>{{ $t('developer.project.submit_pin.actions.submit_pin') }}
                </button>
              </div>
            </div>
          </section>
        </form>
      </div>
    </main>
  </div>
</template>